import ComponentPlaceholder from 'components/ComponentPlaceholder';
import { LayoutContainer } from 'components/Layout';
import RichText from 'components/RichText';
import Text from 'components/Text';
import { useIsInNestedPlaceholder } from 'contexts';
import { useIsEditing } from 'hooks';
import type { ColorField, Field, SitecoreBlockProps } from 'models/sitecore';
import type { HTMLString } from 'types';
import { getEditorMargin } from 'utils/business-logic';
import { cn } from 'utils/classNames';

interface Props extends SitecoreBlockProps {
	fields?: {
		backgroundColor?: ColorField;
		content?: Field<HTMLString>;
		heading?: Field<string>;
	};
}

/**
 * Content box with a colored background. Heading and rich text.
 */
export default function ContentBlockHighlight({ fields, params }: Props) {
	const isEditing = useIsEditing();
	const isInNestedPlaceholder = useIsInNestedPlaceholder();

	if (isEditing) {
		return (
			<ComponentPlaceholder
				componentName="ContentBlockHighlight"
				deprecated="Replace with ContentBlock, now has a severity field. Deadline April 26, 2027 [DP-10003]"
			/>
		);
	}

	if (!fields) {
		return null;
	}

	// No ComponentPlaceholder for the editor since it should be editable inline.
	if (!isEditing && !fields.heading?.value && !fields.content?.value) {
		return null;
	}

	const backgroundColor = fields.backgroundColor?.fields?.colorHex?.value;

	return (
		<LayoutContainer
			id={params?.anchor}
			outerClassName={getEditorMargin(params)}
		>
			<div
				style={backgroundColor ? { backgroundColor } : undefined}
				// Keep the background color class as a default color.
				className={cn(
					'rounded-md bg-information p-6 md:rounded-lg forced-colors:border',
					!isInNestedPlaceholder && 'max-w-col-6 md:p-12',
				)}
			>
				<Text
					as={params?.heading || 'h2'}
					field={fields.heading}
					className="mb-r-sm"
				/>
				<RichText field={fields.content} />
			</div>
		</LayoutContainer>
	);
}
ContentBlockHighlight.displayName = 'ContentBlockHighlight';
